import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { employeesSalesApi } from 'services/apiDigitalTicket/employeesSales';
import { employeesApi } from 'services/apiDigitalTicket/employees';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { listTripsRequest, listTripsSuccess } from 'store/ducks/trips/actions';

// Types
import { ApplicationState } from 'store';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import { CONST_EMPLOYEES_OBJECT } from 'constants/employees';
import PATH from 'constants/basePath';

import * as EmployeesActions from './actions';
import { EmployeesTypes } from './types';
import { TripInterface } from '../trips/types';

function* list({
  payload,
}: ReturnType<typeof EmployeesActions.listEmployeesRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando funcionários' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.listEmployeesSuccess({
          employeesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - listEmployees - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.listEmployeesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listInactive({
  payload,
}: ReturnType<typeof EmployeesActions.listInactiveEmployeesRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({
          title: 'Carregando funcionários inativos',
        }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.listInactiveEmployeesSuccess({
          inactiveEmployeesList: response.data,
          totalCountInactive: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - listEmployees - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.listInactiveEmployeesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof EmployeesActions.getEmployeeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.getEmployeeSuccess({ employeeData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - getEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.getEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof EmployeesActions.filterEmployeesRequest>): any {
  try {
    const { body, filtering } = payload;

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.filterEmployeesSuccess({
          filterEmployeesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!filtering) yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - filterEmployees - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.filterEmployeesFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof EmployeesActions.createEmployeeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando funcionário' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.createEmployeeSuccess({ employeeData: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Funcionário criado com sucesso',
        }),
      );
      if (meta.redirectTo) yield put(push(meta.redirectTo));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - createEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.createEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
  meta,
}: ReturnType<typeof EmployeesActions.updateEmployeeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando funcionário' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.updateEmployeeSuccess({ employeeData: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Funcionário alterado com sucesso',
        }),
      );
      if (meta.dispatchOnSuccess) {
        yield put(
          meta.dispatchOnSuccess.action(meta.dispatchOnSuccess.payload),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - updateEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.updateEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
  meta,
}: ReturnType<typeof EmployeesActions.deleteEmployeeRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo funcionário' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Funcionário excluído com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());

      if (meta.dispatchOnSuccess) {
        yield put(
          meta.dispatchOnSuccess.action(meta.dispatchOnSuccess.payload),
        );
      }

      if (window.location.pathname === PATH.DRIVERS.BASE_URL) {
        yield put(
          EmployeesActions.listEmployeesRequest({
            body: {
              type: CONST_EMPLOYEES_OBJECT.TYPES_EMPLOYEES.DRIVER.KEY,
            },
          }),
        );
        return;
      }

      yield put(push(PATH.DRIVERS.BASE_URL));
    }
  } catch (error) {
    console.error('employees - remove - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Erro ao deletar o funcionário',
      }),
    );

    yield put(EmployeesActions.deleteEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeList({
  payload,
}: ReturnType<typeof EmployeesActions.deleteListEmployeeRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo funcionários' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const errors = [];
    const success = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const transportEmployeeId of payload) {
      try {
        yield call(employeesApi.delete, {
          params: {
            transportOperatorId: transportOperator.id,
            transportEmployeeId,
          },
        });

        success.push({ transportEmployeeId });
      } catch (error) {
        errors.push({ transportEmployeeId, error });
      }
    }

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'success',
        message: `${success.length} funcionarios excluídos com sucesso, e ${errors.length} com falha`,
      }),
    );

    yield put(
      EmployeesActions.listEmployeesRequest({
        body: {
          type: CONST_EMPLOYEES_OBJECT.TYPES_EMPLOYEES.DRIVER.KEY,
        },
      }),
    );

    yield put(push(PATH.DRIVERS.BASE_URL));
    yield put(DialogsActions.setClose());
  } catch (error) {
    console.error('employees - remove - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Erro ao deletar o funcionário',
      }),
    );

    yield put(EmployeesActions.deleteListEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* changePassword({
  payload,
}: ReturnType<typeof EmployeesActions.changePasswordRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Resetando senha' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.changePassword, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(EmployeesActions.changePasswordSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Senha resetada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - Reset Password - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.changePasswordFailure());
    yield put(DialogsActions.setClose());
  }
}

function* addPayment({
  payload,
}: ReturnType<typeof EmployeesActions.addEmployeePaymentRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Efetuando pagamento' }));
    const initialTripList = yield select(
      (state: ApplicationState) => state.trips.tripList || [],
    );
    const tripsListTotalCount = yield select(
      (state: ApplicationState) => state.trips.totalCount,
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesSalesApi.addPayment, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(EmployeesActions.addEmployeePaymentSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pagamento efetuado com sucesso',
        }),
      );
      const tripList = yield initialTripList.map((trip: TripInterface) => {
        if (payload.tripIds.includes(trip.id)) {
          return {
            ...trip,
            EntriesResume: {
              ...trip.EntriesResume,
              owned: 0,
            },
          };
        }
        return trip;
      });

      yield put(
        listTripsSuccess({
          tripList,
          totalCount: tripsListTotalCount,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employee - payment - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.addEmployeePaymentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removePayment({
  payload,
}: ReturnType<typeof EmployeesActions.removeEmployeePaymentRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Extornando pagamento' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const startedAt = yield select(
      (state: ApplicationState) => state.reports.startedAt,
    );
    const response = yield call(employeesApi.removePayment, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(EmployeesActions.removeEmployeePaymentSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pagamento extornado com sucesso',
        }),
      );
      yield put(
        listTripsRequest({
          params: {
            day: new Date(startedAt).getTime(),
            _includes: 'entry,transaction,payment,car',
          },
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employee - payment - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.removeEmployeePaymentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* report({
  payload,
}: ReturnType<typeof EmployeesActions.getEmployeesReportRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.report, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.getEmployeesReportSuccess({
          employeesReports: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - report - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.getEmployeesReportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* dailyReport({
  payload,
}: ReturnType<typeof EmployeesActions.getDailyEmployeeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.dailyReport, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.getDailyEmployeeSuccess({
          employeesDailyReports: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - dailyReport - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.getDailyEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createMany({
  payload,
}: ReturnType<typeof EmployeesActions.createManyEmployeeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando funcionários' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const data = payload.map((obj) => ({
      ...obj,
      transportOperatorId: transportOperator.id,
    }));

    const response = yield call(employeesApi.createMany, {
      data,
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.createManyEmployeeSuccess({
          manyEmployeesList: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Funcionários criados com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - createManyEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.createManyEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getEmployeeSettings({
  payload,
}: ReturnType<typeof EmployeesActions.getEmployeeSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.getEmployeeSettings, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.getEmployeeSettingsSuccess({
          employeeSettingsData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - settings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.getEmployeeSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateEmployeeSettings({
  payload,
}: ReturnType<typeof EmployeesActions.updateEmployeeSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando Motorista' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.updateEmployeeSettings, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.updateEmployeeSettingsSuccess({
          employeeSettingsData: {
            ...response.data,
            paymentMethods: JSON.parse(response.data.paymentMethods),
            singleTicketProfiles: JSON.parse(
              response.data.singleTicketProfiles,
            ),
          },
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Motorista alterado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - updateEmployeeSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.updateEmployeeSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* employeeRestrictionList({
  payload,
  meta,
}: ReturnType<typeof EmployeesActions.employeeRestrictionListRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.employeeRestrictionList, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.employeeRestrictionListSuccess({
          employeeRestrictionList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - employeeRestrictionList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.listEmployeesFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* employeeRating({
  payload,
}: ReturnType<typeof EmployeesActions.employeeRatingRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.employeeRating, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesActions.employeeRatingSuccess({
          employeeRating: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - employeeRating - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesActions.listEmployeesFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const employeesSagas = [
  takeLatest(EmployeesTypes.LIST_EMPLOYEES_REQUEST, list),
  takeLatest(EmployeesTypes.LIST_INACTIVE_EMPLOYEES_REQUEST, listInactive),
  takeLatest(EmployeesTypes.GET_EMPLOYEE_REQUEST, get),
  takeLatest(EmployeesTypes.FILTER_EMPLOYEES_REQUEST, filter),
  takeLatest(EmployeesTypes.CREATE_EMPLOYEE_REQUEST, create),
  takeLatest(EmployeesTypes.UPDATE_EMPLOYEE_REQUEST, update),
  takeLatest(EmployeesTypes.DELETE_EMPLOYEE_REQUEST, remove),
  takeLatest(EmployeesTypes.DELETE_LIST_EMPLOYEE_REQUEST, removeList),
  takeLatest(EmployeesTypes.CHANGE_PASSWORD_REQUEST, changePassword),
  takeLatest(EmployeesTypes.ADD_EMPLOYEE_PAYMENT_REQUEST, addPayment),
  takeLatest(EmployeesTypes.REMOVE_EMPLOYEE_PAYMENT_REQUEST, removePayment),
  takeLatest(EmployeesTypes.GET_EMPLOYEES_REPORT_REQUEST, report),
  takeLatest(EmployeesTypes.GET_EMPLOYEES_DAILY_REPORT_REQUEST, dailyReport),
  takeLatest(EmployeesTypes.CREATE_MANY_EMPLOYEE_REQUEST, createMany),
  takeLatest(EmployeesTypes.GET_EMPLOYEE_SETTINGS_REQUEST, getEmployeeSettings),
  takeLatest(
    EmployeesTypes.UPDATE_EMPLOYEE_SETTINGS_REQUEST,
    updateEmployeeSettings,
  ),
  takeLatest(
    EmployeesTypes.EMPLOYEE_RESTRICTION_LIST_REQUEST,
    employeeRestrictionList,
  ),
  takeLatest(EmployeesTypes.EMPLOYEE_RATING_REQUEST, employeeRating),
];
